import { EQUIPMENT, FEEDERLISTS } from "../_constants/actionConstants";
import _ from "lodash";

const eqInitialState = {
  projEquipLists: {}, // Normalized by projectId — used by Project Admin tab
  // Normalized by projectId — same as projEquipLists but also includes merged-away (inactive)
  // equipment. Read-only use only (ADR review page, PDF export) — never for selection UIs.
  projEquipListsIncludingInactive: {},
  allAvailableEquipLists: {}, // Normalized by projectId — every contractor's full catalog, linked or not
  contractorCatalogEquip: {}, // Normalized by contractorId — used by ADR equipment dropdown
  contractorCatalogLoading: {}, // Normalized by contractorId — true while its catalog is being fetched
  openEquipmentDetails: {}, // Kept for backward compat; no longer actively populated
  miscSessionData: {},
  fetching: false,
};

/**
 * Normalizes a ProjectEquipmentListDto to the backward-compatible shape used
 * by ADR components, report selectors, and other consumers that expect
 * equipmentId, number, make, model, equipClass, numberMakeDesc, makeDesc.
 */
const toEquipState = (pe) => ({
  ...pe,
  realEquipmentId: pe.equipmentId,
  equipmentId: pe.projectEquipmentId,
  number: pe.equipmentNumber,
  make: pe.equipmentMake || "",
  model: pe.equipmentModel || "",
  description: pe.description || "",
  equipClass: pe.equipmentClass || "",
  numberMakeDesc: pe.numberDesc || "",
  makeDesc:
    [pe.equipmentMake, pe.equipmentModel].filter(Boolean).join(" ") ||
    pe.equipmentModelDescription ||
    pe.description ||
    "",
});

const normalizeList = (list) =>
  Array.isArray(list) ? list.map(toEquipState) : list;

export const reducer = (state = eqInitialState, action) => {
  switch (action.type) {
    case EQUIPMENT.ADD:
    case EQUIPMENT.DELETE:
    case EQUIPMENT.EDIT:
    case EQUIPMENT.FETCH_ALL:
    case EQUIPMENT.FETCH_ALL_AVAILABLE:
    case EQUIPMENT.FETCH_ALL_INCLUDING_INACTIVE:
    case EQUIPMENT.FETCH_DETAILS:
    case EQUIPMENT.GENERIC_REQUEST:
    case EQUIPMENT.MERGE:
    case EQUIPMENT.LINK_TO_PROJECT:
      return { ...state, fetching: true };

    case EQUIPMENT.FETCH_ALL_AVAILABLE_REJECTED:
    case EQUIPMENT.FETCH_ALL_INCLUDING_INACTIVE_REJECTED:
    case EQUIPMENT.LINK_TO_PROJECT_REJECTED:
    case EQUIPMENT.FETCH_ALL_REJECTED:
    case EQUIPMENT.FETCH_DETAILS_REJECTED:
    case EQUIPMENT.DOWNLOAD_ALL_REJECTED:
    case EQUIPMENT.ADD_REJECTED:
    case EQUIPMENT.EDIT_REJECTED:
    case EQUIPMENT.DELETE_REJECTED:
      return { ...state, fetching: false };

    case EQUIPMENT.MERGE_FULFILLED: {
      const { sourceProjectEquipmentId, contractorId } = action;
      const existingContractorCatalog = state.contractorCatalogEquip[contractorId];

      // contractorCatalogEquip (ADR equipment dropdown) is fetched once per contractor per
      // session and never refetched on its own — without pruning the merged-away source here,
      // it keeps showing as a choosable option until a full page reload clears the cache.
      const contractorCatalogEquip =
        existingContractorCatalog && sourceProjectEquipmentId
          ? {
              ...state.contractorCatalogEquip,
              [contractorId]: existingContractorCatalog.filter(
                (e) => e.equipmentId !== sourceProjectEquipmentId
              ),
            }
          : state.contractorCatalogEquip;

      return { ...state, fetching: false, contractorCatalogEquip };
    }
    case EQUIPMENT.MERGE_REJECTED:
      return { ...state, fetching: false };

    case EQUIPMENT.GENERIC_REQUEST_FULFILLED:
    case EQUIPMENT.GENERIC_REQUEST_REJECTED:
      return { ...state, fetching: false };

    case EQUIPMENT.FETCH_DETAILS_FULFILLED: {
      const { projectId, equipmentId, equipmentDetails } = action;
      if (!projectId || !equipmentId || !equipmentDetails)
        return { ...state, fetching: false };

      return {
        ...state,
        fetching: false,
        openEquipmentDetails: {
          ...state.openEquipmentDetails,
          [projectId]: {
            ...state.openEquipmentDetails[projectId],
            [equipmentId]: equipmentDetails,
          },
        },
      };
    }

    case EQUIPMENT.CLEAR_DETAILS: {
      const { projectId, equipmentId } = action;
      if (
        !projectId ||
        !equipmentId ||
        !(projectId in state.openEquipmentDetails) ||
        !(equipmentId in state.openEquipmentDetails[projectId])
      )
        return { ...state, fetching: false };

      let openEquipmentDetails = _.cloneDeep(state.openEquipmentDetails);
      delete openEquipmentDetails[projectId][equipmentId];

      return { ...state, fetching: false, openEquipmentDetails };
    }

    case EQUIPMENT.ADD_FULFILLED: {
      const { projectId, equipment } = action;
      if (!projectId || !equipment) return { ...state, fetching: false };
      const existingList = state.projEquipLists[projectId] || [];

      // Also keep contractorCatalogEquip in sync — it's a separate cache (keyed by
      // contractorId, used by the ADR equipment dropdown) that getForContractor() only
      // ever populates once per contractor per session. Without this, equipment added here
      // via Project Admin never appears in the ADR dropdown until a full page reload clears
      // that cache. Only append if that contractor's catalog is already loaded (non-empty) —
      // otherwise leave it alone so the eventual real fetch populates it in full.
      const existingContractorCatalog = state.contractorCatalogEquip[equipment.contractorId];
      const contractorCatalogEquip =
        existingContractorCatalog && existingContractorCatalog.length > 0
          ? {
              ...state.contractorCatalogEquip,
              [equipment.contractorId]: [...existingContractorCatalog, equipment],
            }
          : state.contractorCatalogEquip;

      // Same rationale as contractorCatalogEquip above: getAllIncludingInactive() also only
      // fetches once per project per session (see its cache-and-skip check). Without this, an
      // equipment-set row added here resolves to contractorId 0 — and a blank, empty equipment
      // dropdown — the next time this ADR is loaded from the server this session, because
      // adrFactories.js resolves a row's contractorId by looking its equipmentId up in this list.
      const existingIncludingInactive = state.projEquipListsIncludingInactive[projectId];
      const projEquipListsIncludingInactive =
        existingIncludingInactive && existingIncludingInactive.length > 0
          ? {
              ...state.projEquipListsIncludingInactive,
              [projectId]: [...existingIncludingInactive, equipment],
            }
          : state.projEquipListsIncludingInactive;

      return {
        ...state,
        fetching: false,
        projEquipLists: {
          ...state.projEquipLists,
          [projectId]: [...existingList, equipment],
        },
        contractorCatalogEquip,
        projEquipListsIncludingInactive,
      };
    }

    case EQUIPMENT.DELETE_FULFILLED: {
      const { projectId, equipmentId, contractorId } = action;
      if (!projectId || !equipmentId) return { ...state, fetching: false };

      const existingList = state.projEquipLists[projectId] || [];

      // Same rationale as ADD_FULFILLED/MERGE_FULFILLED: contractorCatalogEquip is a separate,
      // once-per-session cache backing the ADR equipment dropdown. Without pruning it here, a
      // deleted equipment entry stays choosable there for the rest of the session — selecting it
      // would reference a ProjectEquipmentId that no longer exists in the DB.
      const existingContractorCatalog = state.contractorCatalogEquip[contractorId];
      const contractorCatalogEquip =
        existingContractorCatalog && contractorId
          ? {
              ...state.contractorCatalogEquip,
              [contractorId]: existingContractorCatalog.filter(
                (e) => e.equipmentId !== equipmentId
              ),
            }
          : state.contractorCatalogEquip;

      return {
        ...state,
        fetching: false,
        projEquipLists: {
          ...state.projEquipLists,
          [projectId]: existingList.filter((e) => e.equipmentId !== equipmentId),
        },
        contractorCatalogEquip,
      };
    }

    case EQUIPMENT.FETCH_ALL_FULFILLED:
    case FEEDERLISTS.FETCH_FULFILLED: {
      const { projectId, projEquipList } = action;
      if (!projectId || !projEquipList) return { ...state, fetching: false };

      return {
        ...state,
        fetching: false,
        projEquipLists: {
          ...state.projEquipLists,
          [projectId]: normalizeList(projEquipList),
        },
      };
    }

    case EQUIPMENT.FETCH_ALL_INCLUDING_INACTIVE_FULFILLED: {
      const { projectId, projEquipListIncludingInactive } = action;
      if (!projectId || !projEquipListIncludingInactive) return { ...state, fetching: false };

      return {
        ...state,
        fetching: false,
        projEquipListsIncludingInactive: {
          ...state.projEquipListsIncludingInactive,
          [projectId]: normalizeList(projEquipListIncludingInactive),
        },
      };
    }

    case EQUIPMENT.FETCH_ALL_AVAILABLE_FULFILLED: {
      const { projectId, allAvailableEquipList } = action;
      if (!projectId || !allAvailableEquipList) return { ...state, fetching: false };

      // Already normalized by the action creator (toAvailableEquipState) — do not
      // re-normalize here, that duplication is exactly what caused a prior bug
      // (see toEquipState's own duplication note).
      return {
        ...state,
        fetching: false,
        allAvailableEquipLists: {
          ...state.allAvailableEquipLists,
          [projectId]: allAvailableEquipList,
        },
      };
    }

    case EQUIPMENT.LINK_TO_PROJECT_FULFILLED: {
      const { projectId, equipmentId, equipment } = action;
      if (!projectId || !equipment) return { ...state, fetching: false };

      const existingList = state.projEquipLists[projectId] || [];
      const existingAvailable = state.allAvailableEquipLists[projectId] || [];
      // Equipment already in the project (e.g. re-linked via a stale/disabled button,
      // or a race) resolves to the same ProjectEquipmentId — replace, don't duplicate.
      const alreadyPresent = existingList.some((e) => e.equipmentId === equipment.equipmentId);
      const updatedList = alreadyPresent
        ? existingList.map((e) => (e.equipmentId === equipment.equipmentId ? equipment : e))
        : [...existingList, equipment];

      return {
        ...state,
        fetching: false,
        projEquipLists: {
          ...state.projEquipLists,
          [projectId]: updatedList,
        },
        allAvailableEquipLists: {
          ...state.allAvailableEquipLists,
          [projectId]: existingAvailable.map((e) =>
            e.equipmentId === equipmentId ? { ...e, isLinked: true, projectEquipmentId: equipment.projectEquipmentId } : e
          ),
        },
      };
    }

    case EQUIPMENT.FETCH_CONTRACTOR_CATALOG: {
      const { contractorId } = action;
      if (!contractorId) return state;

      return {
        ...state,
        contractorCatalogLoading: { ...state.contractorCatalogLoading, [contractorId]: true },
      };
    }

    case EQUIPMENT.FETCH_CONTRACTOR_CATALOG_FULFILLED: {
      const { contractorId, contractorEquipList } = action;
      if (!contractorId || !contractorEquipList) return state;

      return {
        ...state,
        contractorCatalogEquip: {
          ...state.contractorCatalogEquip,
          [contractorId]: contractorEquipList,
        },
        contractorCatalogLoading: { ...state.contractorCatalogLoading, [contractorId]: false },
      };
    }

    case EQUIPMENT.FETCH_CONTRACTOR_CATALOG_REJECTED: {
      const { contractorId } = action;
      if (!contractorId) return state;

      return {
        ...state,
        contractorCatalogLoading: { ...state.contractorCatalogLoading, [contractorId]: false },
      };
    }

    case EQUIPMENT.EDIT_FULFILLED: {
      const { projectId, equipmentId, equipmentDetails } = action;
      if (!projectId || !equipmentId || !equipmentDetails)
        return { ...state, fetching: false };

      const existingList = state.projEquipLists[projectId] || [];
      const updatedList = existingList.map((e) =>
        e.equipmentId === equipmentId ? equipmentDetails : e
      );

      // Same rationale as ADD_FULFILLED/MERGE_FULFILLED/DELETE_FULFILLED: without updating
      // contractorCatalogEquip here too, the ADR equipment dropdown keeps showing this
      // equipment's old Number/Class/Make/Model for the rest of the session after an edit
      // (contractorId is fixed at creation, so it can't have changed here — same bucket).
      const existingContractorCatalog = state.contractorCatalogEquip[equipmentDetails.contractorId];
      const contractorCatalogEquip =
        existingContractorCatalog
          ? {
              ...state.contractorCatalogEquip,
              [equipmentDetails.contractorId]: existingContractorCatalog.map((e) =>
                e.equipmentId === equipmentId ? equipmentDetails : e
              ),
            }
          : state.contractorCatalogEquip;

      return {
        ...state,
        fetching: false,
        projEquipLists: {
          ...state.projEquipLists,
          [projectId]: updatedList,
        },
        openEquipmentDetails: {
          ...state.openEquipmentDetails,
          [projectId]: {
            ...state.openEquipmentDetails[projectId],
            [equipmentId]: equipmentDetails,
          },
        },
        contractorCatalogEquip,
      };
    }

    default:
      return state;
  }
};
